import React from "react";
import { ImportantNote, KeyTerm, Mnemonic } from "../note-components";
import { LatexText } from "../../../components/ui/latex-text";
import type { UnitNotes } from "../types";

const calculusAbUnit1Notes: UnitNotes = {
  unitId: "unit-1",
  unitName: "Unit 1: Limits and Continuity",
  examWeight: "10–12%",
  focus: "Change, Limits, Analysis of Functions",
  sections: [
    {
      id: "topic-1-1",
      title: "Topic 1.1: Introducing Calculus: Can Change Occur at an Instant?",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Central Problem</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Pre-calculus mathematics deals with <strong>Average Rate of Change</strong> (slope of a secant line). Calculus deals with <strong>Instantaneous Rate of Change</strong> (slope of a tangent line).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Key Concepts</h3>
            <div className="space-y-4 mb-4">
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-100">
                <h4 className="font-semibold text-slate-900">Secant Line</h4>
                <p className="text-sm text-slate-600 mt-1">
                  A line connecting two points on a curve, <LatexText text="$(x, f(x))$" /> and <LatexText text="$(x+h, f(x+h))$" />. Its slope represents the average rate of change over the interval <LatexText text="$h$" />.
                </p>
                <div className="mt-3 bg-white p-3 rounded border border-slate-200">
                  <LatexText text="$$m_{sec} = \frac{f(x+h) - f(x)}{h}$$" />
                </div>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-900">The Problem</h4>
                <p className="text-sm text-slate-700 mt-1">
                  To find the slope at a single instant, we want <LatexText text="$h$" /> to be 0. However, dividing by zero is undefined.
                </p>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-semibold text-green-900">The Solution</h4>
                <p className="text-sm text-slate-700 mt-1">
                  We use a <strong>Limit</strong>. We examine what happens as <LatexText text="$h$" /> gets arbitrarily close to 0 without actually <em>being</em> 0.
                </p>
                <div className="mt-3 bg-white p-3 rounded border border-green-200">
                  <LatexText text="$$m_{tan} = \lim_{h \to 0} \frac{f(x+h) - f(x)}{h}$$" />
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-2",
      title: "Topic 1.2: Defining Limits and Using Limit Notation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Definition of a Limit</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The limit of <LatexText text="$f(x)$" /> as <LatexText text="$x$" /> approaches <LatexText text="$c$" /> is <LatexText text="$L$" /> if <LatexText text="$f(x)$" /> gets arbitrarily close to <LatexText text="$L$" /> as <LatexText text="$x$" /> gets sufficiently close to <LatexText text="$c$" /> (from both sides), but <LatexText text="$x \neq c$" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <h4 className="font-bold text-blue-900">Notation</h4>
              <div className="mt-2">
                <LatexText text="$$\lim_{x \to c} f(x) = L$$" />
              </div>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Crucial Distinctions</h3>
            <div className="space-y-3 mb-4">
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="font-bold text-amber-800">Value vs. Limit</h4>
                <p className="text-sm text-slate-700 mt-1">
                  <LatexText text="$f(c)$" /> is the value of the function <strong>at</strong> <LatexText text="$x=c$" />. The limit is the value the function <strong>approaches</strong> as <LatexText text="$x$" /> gets close to <LatexText text="$c$" />. It is possible for <LatexText text="$\lim_{x \to c} f(x)$" /> to exist even if <LatexText text="$f(c)$" /> is undefined.
                </p>
              </div>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <h4 className="font-bold text-slate-800">One-Sided Limits</h4>
                <ul className="list-disc pl-5 space-y-2 text-slate-700 mt-2">
                  <li><strong>Left-hand limit:</strong> <LatexText text="$\lim_{x \to c^-} f(x)$" /> (approaching <LatexText text="$c$" /> from values smaller than <LatexText text="$c$" />)</li>
                  <li><strong>Right-hand limit:</strong> <LatexText text="$\lim_{x \to c^+} f(x)$" /> (approaching <LatexText text="$c$" /> from values larger than <LatexText text="$c$" />)</li>
                </ul>
              </div>
            </div>

            <ImportantNote>
              <strong>Existence of a General Limit:</strong> For the general limit <LatexText text="$\lim_{x \to c} f(x)$" /> to exist, the left-hand limit must equal the right-hand limit:
              <div className="mt-2">
                <LatexText text="$$\lim_{x \to c^-} f(x) = \lim_{x \to c^+} f(x) = L$$" />
              </div>
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-3",
      title: "Topic 1.3: Estimating Limit Values from Graphs",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">How to Analyze</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Trace the graph with your eyes (or fingers) from the left and right sides toward the <LatexText text="$x$" />-value in question.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Scenarios</h3>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">1. Continuous Path</h4>
                <p className="text-sm text-slate-700 mt-1">The curve passes smoothly through the point. The limit is simply the <LatexText text="$y$" />-value.</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">2. Hole (Removable Discontinuity)</h4>
                <p className="text-sm text-slate-700 mt-1">The graph approaches a specific <LatexText text="$y$" />-value from both sides, but there is an open circle at that point. <strong>The limit exists</strong> and equals the height of the hole.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">3. Jump</h4>
                <p className="text-sm text-slate-700 mt-1">The graph approaches one <LatexText text="$y$" />-value from the left and a different <LatexText text="$y$" />-value from the right. <strong>The limit does not exist (DNE).</strong></p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">4. Vertical Asymptote</h4>
                <p className="text-sm text-slate-700 mt-1">The graph shoots up to <LatexText text="$\infty$" /> or down to <LatexText text="$-\infty$" />. <strong>The limit DNE</strong> (though we often describe the behavior as <LatexText text="$\infty$" /> or <LatexText text="$-\infty$" />).</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-4",
      title: "Topic 1.4: Estimating Limit Values from Tables",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Method</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Create a table of values for <LatexText text="$x$" /> that get incrementally closer to <LatexText text="$c$" /> from both sides.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200 mb-4">
              <h4 className="font-bold text-slate-800 mb-2">Example: Find <LatexText text="$\lim_{x \to 3} f(x)$" /></h4>
              <ul className="list-disc pl-5 space-y-1 text-slate-700 text-sm">
                <li><strong>Left Approach (<LatexText text="$x \to 3^-$" />):</strong> Plug in 2.9, 2.99, 2.999</li>
                <li><strong>Right Approach (<LatexText text="$x \to 3^+$" />):</strong> Plug in 3.1, 3.01, 3.001</li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Interpretation</h3>
            <p className="text-slate-700 leading-relaxed">
              If the resulting <LatexText text="$y$" />-values converge on a specific number (e.g., 4.999... and 5.001... suggest 5), that is the estimated limit. If the numbers diverge or oscillate wildly, the limit may not exist.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-5",
      title: "Topic 1.5: Determining Limits Using Algebraic Properties",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Limit Laws</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If limits exist, they behave nicely with arithmetic operations. Let <LatexText text="$\lim_{x \to c} f(x) = L$" /> and <LatexText text="$\lim_{x \to c} g(x) = M$" />.
            </p>
            <div className="space-y-3 mb-4">
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Sum/Difference:</strong> <LatexText text="$\lim [f(x) \pm g(x)] = L \pm M$" /></p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Product:</strong> <LatexText text="$\lim [f(x) \cdot g(x)] = L \cdot M$" /></p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Constant Multiple:</strong> <LatexText text="$\lim [k \cdot f(x)] = k \cdot L$" /></p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Quotient:</strong> <LatexText text="$\lim \left[\frac{f(x)}{g(x)}\right] = \frac{L}{M}$" />, provided <LatexText text="$M \neq 0$" /></p>
              </div>
              <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                <p className="text-slate-700"><strong>Power:</strong> <LatexText text="$\lim [f(x)]^n = L^n$" /></p>
              </div>
            </div>

            <ImportantNote>
              <strong>Strategy:</strong> Always try <strong>Direct Substitution</strong> first. If the function is continuous at <LatexText text="$c$" />, the limit is simply <LatexText text="$f(c)$" />.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-6",
      title: "Topic 1.6: Determining Limits Using Algebraic Manipulation",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Indeterminate Form</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If direct substitution results in <LatexText text="$\frac{0}{0}$" />, this is an <strong>indeterminate form</strong>. It essentially means "we don't know yet; do more work." It suggests a "hole" in the graph.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Manipulation Techniques</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">1. Factoring</h4>
                <p className="text-sm text-slate-700 mt-1">Factor numerator and denominator, cancel common factors, then retry substitution.</p>
                <div className="mt-3 bg-white p-3 rounded border border-blue-200">
                  <p className="text-sm text-blue-700 mb-2 italic">Example: lim(x→2) (x²-4)/(x-2) = lim(x→2) [(x-2)(x+2)]/(x-2) = lim(x→2) (x+2) = 4</p>
                  <LatexText text="$$\lim_{x \to 2} \frac{x^2-4}{x-2} = \lim_{x \to 2} \frac{(x-2)(x+2)}{x-2} = \lim_{x \to 2} (x+2) = 4$$" />
                </div>
              </div>
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800">2. Conjugates</h4>
                <p className="text-sm text-slate-700 mt-1">If a radical is involved (e.g., <LatexText text="$\sqrt{x} - 2$" />), multiply the numerator and denominator by the conjugate (<LatexText text="$\sqrt{x} + 2$" />).</p>
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-bold text-purple-800">3. Simplifying Complex Fractions</h4>
                <p className="text-sm text-slate-700 mt-1">Find a common denominator to combine fractions, allowing terms to cancel.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-7",
      title: "Topic 1.7: Selecting Procedures for Determining Limits",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Decision Flowchart for <LatexText text="$\lim_{x \to c} f(x)$" /></h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Try Direct Substitution</h4>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-green-800">Result is a Number <LatexText text="$L$" /></h4>
                  <p className="text-sm text-slate-700 mt-1">The limit is <LatexText text="$L$" />.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-red-50 rounded-lg border border-red-200">
                <span className="bg-red-200 text-red-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-red-800">Result is <LatexText text="$\frac{\text{Non-Zero Number}}{0}$" /></h4>
                  <p className="text-sm text-slate-700 mt-1">There is a Vertical Asymptote. The limit is DNE (check signs to see if it is <LatexText text="$\pm \infty$" />).</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                <span className="bg-amber-200 text-amber-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">4</span>
                <div>
                  <h4 className="font-semibold text-amber-800">Result is <LatexText text="$\frac{0}{0}$" /></h4>
                  <p className="text-sm text-slate-700 mt-1">Indeterminate. Use algebraic manipulation (Topic 1.6) or trigonometric identities.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-8",
      title: "Topic 1.8: Determining Limits Using the Squeeze Theorem",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Concept (The Sandwich Theorem)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If a function <LatexText text="$f(x)$" /> is "squeezed" between two other functions <LatexText text="$g(x)$" /> and <LatexText text="$h(x)$" /> near <LatexText text="$c$" />, and the limits of the outer functions are equal, then <LatexText text="$f(x)$" /> must approach that same limit.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Formal Statement</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$g(x) \leq f(x) \leq h(x)$" /> for all <LatexText text="$x$" /> near <LatexText text="$c$" /> (except possibly at <LatexText text="$c$" />), and:
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 mb-4">
              <LatexText text="$$\lim_{x \to c} g(x) = \lim_{x \to c} h(x) = L$$" />
              <p className="text-sm text-slate-700 mt-3">Then:</p>
              <LatexText text="$$\lim_{x \to c} f(x) = L$$" />
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Important Special Trig Limits</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              (Derived via Squeeze Theorem)
            </p>
            <div className="space-y-3">
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <LatexText text="$$\lim_{x \to 0} \frac{\sin x}{x} = 1$$" />
              </div>
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <LatexText text="$$\lim_{x \to 0} \frac{1 - \cos x}{x} = 0$$" />
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-9",
      title: "Topic 1.9: Connecting Multiple Representations of Limits",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Fluency in Representations</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Calculus requires fluency in moving between representations. You must be able to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>Given a <strong>graph</strong>, write the limit statement</li>
              <li>Given an <strong>equation</strong> (piecewise or rational), sketch the graph and find the limit</li>
              <li>Given a <strong>table</strong>, recognize the pattern and predict the limit</li>
            </ul>

            <ImportantNote>
              <strong>Note on Piecewise Functions:</strong> To find the limit at the "break" point of a piecewise function, you must calculate the one-sided limit from the top equation and the one-sided limit from the bottom equation. If they match, the limit exists.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-10",
      title: "Topic 1.10: Exploring Types of Discontinuities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Continuity (Informal)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              You can draw the graph without lifting your pencil.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Types of Discontinuities</h3>
            <div className="space-y-3">
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800">1. Removable (Point) Discontinuity</h4>
                <p className="text-sm text-slate-700 mt-1">A "hole" in the graph. Occurs when algebraic factors cancel. Limit exists, but function is undefined or not equal to the limit.</p>
              </div>
              <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                <h4 className="font-bold text-orange-800">2. Jump Discontinuity</h4>
                <p className="text-sm text-slate-700 mt-1">The graph "jumps" from one <LatexText text="$y$" />-value to another. Occurs often in piecewise functions or step functions (like <LatexText text="$\frac{|x|}{x}$" />). Left limit ≠ Right limit.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800">3. Infinite Discontinuity</h4>
                <p className="text-sm text-slate-700 mt-1">A vertical asymptote. Limit goes to <LatexText text="$\pm \infty$" />.</p>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-11",
      title: "Topic 1.11: Defining Continuity at a Point",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The 3-Part Definition (Memorize This!)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              A function <LatexText text="$f$" /> is continuous at <LatexText text="$x=c$" /> if and only if:
            </p>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800"><LatexText text="$f(c)$" /> is defined</h4>
                  <p className="text-sm text-slate-600 mt-1">The point exists.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800"><LatexText text="$\lim_{x \to c} f(x)$" /> exists</h4>
                  <p className="text-sm text-slate-600 mt-1">The left and right approach the same value.</p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-slate-800"><LatexText text="$\lim_{x \to c} f(x) = f(c)$" /></h4>
                  <p className="text-sm text-slate-600 mt-1">The limit equals the function value.</p>
                </div>
              </div>
            </div>

            <ImportantNote>
              <strong>On the AP Exam:</strong> If asked to justify continuity, you must explicitly state all three conditions.
            </ImportantNote>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-12",
      title: "Topic 1.12: Confirming Continuity over an Interval",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Continuity on Intervals</h3>
            <ul className="list-disc pl-6 space-y-2 text-slate-700 mb-4">
              <li>A function is continuous on an open interval <LatexText text="$(a, b)$" /> if it is continuous at every point in that interval.</li>
              <li>A function is continuous on a closed interval <LatexText text="$[a, b]$" /> if it is continuous on <LatexText text="$(a, b)$" />, continuous from the right at <LatexText text="$a$" />, and continuous from the left at <LatexText text="$b$" />.</li>
            </ul>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Known Continuous Functions</h3>
            <p className="text-slate-700 leading-relaxed">
              Polynomials, rational functions, root functions, and trigonometric functions are continuous at every point in their <strong>domain</strong>.
            </p>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-13",
      title: "Topic 1.13: Removing Discontinuities",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Removable Discontinuities</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              When a function has a <strong>Removable Discontinuity</strong> (a hole) at <LatexText text="$x=c$" /> (usually resulting from a <LatexText text="$0/0$" /> indeterminate form), we can make the function continuous by defining (or redefining) the value of <LatexText text="$f(c)$" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Procedure</h3>
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">1</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Find the limit</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$L = \lim_{x \to c} f(x)$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                <span className="bg-slate-200 text-slate-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">2</span>
                <div>
                  <h4 className="font-semibold text-slate-800">Define the function value</h4>
                  <p className="text-sm text-slate-600 mt-1"><LatexText text="$f(c) = L$" /></p>
                </div>
              </div>
              <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg border border-green-200">
                <span className="bg-green-200 text-green-800 font-bold rounded-full w-8 h-8 flex items-center justify-center text-sm shrink-0">3</span>
                <div>
                  <h4 className="font-semibold text-green-800">Result</h4>
                  <p className="text-sm text-slate-700 mt-1">The "extended function" is now continuous at <LatexText text="$c$" />.</p>
                </div>
              </div>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-14",
      title: "Topic 1.14: Connecting Infinite Limits and Vertical Asymptotes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Infinite Limits</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$\lim_{x \to c} f(x) = \infty$" /> or <LatexText text="$-\infty$" />, this describes the unbounded behavior of the function.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Vertical Asymptotes (VA)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The line <LatexText text="$x=c$" /> is a VA if at least one of the one-sided limits at <LatexText text="$c$" /> is <LatexText text="$\infty$" /> or <LatexText text="$-\infty$" />.
            </p>
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-bold text-blue-900">Finding VAs</h4>
              <p className="text-sm text-slate-700 mt-2">In a rational function, first simplify the expression (cancel common factors). Any factor remaining in the <strong>denominator</strong> set to zero yields a Vertical Asymptote.</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-15",
      title: "Topic 1.15: Connecting Limits at Infinity and Horizontal Asymptotes",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">End Behavior</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Limits at infinity describe what the graph does as <LatexText text="$x$" /> gets huge (<LatexText text="$\infty$" />) or very negative (<LatexText text="$-\infty$" />).
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Horizontal Asymptotes (HA)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              The line <LatexText text="$y = L$" /> is a HA if <LatexText text="$\lim_{x \to \infty} f(x) = L$" /> or <LatexText text="$\lim_{x \to -\infty} f(x) = L$" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Rules for Rational Functions <LatexText text="$\left(\frac{P(x)}{Q(x)}\right)$" /></h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              Compare the degree of the numerator (<LatexText text="$n$" />) and denominator (<LatexText text="$d$" />).
            </p>
            <div className="space-y-3">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="font-bold text-green-800"><LatexText text="$n < d$" /> (Bottom Heavy)</h4>
                <p className="text-sm text-slate-700 mt-1">Limit is <strong>0</strong>. (<LatexText text="$y=0$" /> is HA)</p>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-bold text-blue-800"><LatexText text="$n = d$" /> (Equal Degree)</h4>
                <p className="text-sm text-slate-700 mt-1">Limit is the <strong>ratio of leading coefficients</strong>.</p>
              </div>
              <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                <h4 className="font-bold text-red-800"><LatexText text="$n > d$" /> (Top Heavy)</h4>
                <p className="text-sm text-slate-700 mt-1">Limit is <LatexText text="$\pm \infty$" />. (No HA; possibly a slant asymptote)</p>
              </div>
            </div>

            <h4 className="text-lg font-bold text-slate-800 mb-3 mt-6">Non-Rational Functions</h4>
            <p className="text-slate-700 leading-relaxed mb-4">
              Consider which part of the function "dominates" or grows fastest.
            </p>
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <p className="text-sm text-slate-700"><strong>Growth Hierarchy:</strong> Logs &lt; Polynomials &lt; Exponentials</p>
            </div>
          </section>
        </div>
      )
    },
    {
      id: "topic-1-16",
      title: "Topic 1.16: Working with the Intermediate Value Theorem (IVT)",
      content: (
        <div className="space-y-6">
          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">The Theorem (Existence Theorem)</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              If <LatexText text="$f$" /> is <strong>continuous</strong> on the closed interval <LatexText text="$[a, b]$" /> and <LatexText text="$k$" /> is any number between <LatexText text="$f(a)$" /> and <LatexText text="$f(b)$" />, then there must exist at least one number <LatexText text="$c$" /> in the interval <LatexText text="$(a, b)$" /> such that <LatexText text="$f(c) = k$" />.
            </p>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Justification on AP Exam</h3>
            <p className="text-slate-700 leading-relaxed mb-4">
              To apply IVT, you must write:
            </p>
            <div className="space-y-2 text-slate-700 mb-4">
              <p>1. "<LatexText text="$f$" /> is continuous on <LatexText text="$[a, b]$" />" (State the condition)</p>
              <p>2. <LatexText text="$f(a) < k < f(b)$" /> (Show that <LatexText text="$k$" /> is between the endpoints)</p>
              <p>3. Therefore, by IVT, there exists a <LatexText text="$c \in (a, b)$" /> such that <LatexText text="$f(c) = k$" /></p>
            </div>
          </section>

          <section>
            <h3 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Common Use</h3>
            <p className="text-slate-700 leading-relaxed">
              Proving a function has a zero (root). If <LatexText text="$f(a)$" /> is positive and <LatexText text="$f(b)$" /> is negative (and <LatexText text="$f$" /> is continuous), the function <em>must</em> cross the x-axis (<LatexText text="$f(c)=0$" />) somewhere in between.
            </p>
          </section>
        </div>
      )
    }
  ]
};

export default calculusAbUnit1Notes;
